import { useCallback, useEffect, useState } from 'react';
import { useSettings } from '../../settings/SettingsContext';
import { canSpeak, speak, stopSpeaking } from '../../games/speak';
import { playCorrectPing } from '../../games/ping';
import type { SupervisorScoredConfig } from '../types';

export default function SupervisorScored({ config }: { config: SupervisorScoredConfig }) {
  const { settings } = useSettings();
  const [idx, setIdx] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [incorrect, setIncorrect] = useState(0);

  const total = config.prompts.length;
  const current = total > 0 ? config.prompts[idx % total] : null;

  const playPrompt = useCallback(() => {
    if (!current) return;
    if (config.promptMode === 'AUDIO' && canSpeak()) {
      speak(current.prompt, { voiceName: settings.ttsVoice, rate: settings.ttsRate });
    }
  }, [config.promptMode, current, settings.ttsVoice, settings.ttsRate]);

  useEffect(() => {
    const t = window.setTimeout(playPrompt, 350);
    return () => window.clearTimeout(t);
  }, [playPrompt]);

  useEffect(() => () => stopSpeaking(), []);

  function score(ok: boolean) {
    if (ok) {
      setCorrect((c) => c + 1);
      if (settings.correctPingEnabled) playCorrectPing();
    } else {
      setIncorrect((c) => c + 1);
    }
    setIdx((i) => i + 1);
  }

  if (!current) {
    return <div className="card"><p className="muted">No prompts configured.</p></div>;
  }

  return (
    <div className="card">
      <div className="muted" style={{ marginBottom: 6 }}>
        Prompt {(idx % total) + 1} of {total}
        {' · '}✓ {correct}
        {' · '}✗ {incorrect}
      </div>
      <div className="concept-prompt-big">{current.prompt}</div>
      {current.hint && (
        <div className="muted" style={{ textAlign: 'center', marginBottom: 12 }}>{current.hint}</div>
      )}
      <div className="toolbar" style={{ justifyContent: 'center' }}>
        <button className="btn" onClick={() => score(true)} aria-label="Mark correct">✓ Correct</button>
        <button className="btn secondary" onClick={() => score(false)} aria-label="Mark incorrect">✗ Incorrect</button>
        <button className="btn ghost" onClick={playPrompt} aria-label="Repeat prompt">Repeat</button>
      </div>
    </div>
  );
}
